import React, { useState, useEffect, useRef } from 'react';
import { Zap, Shield, TrendingUp, Users, Activity, Target, ArrowRight, X, Check, Code, Layers, GitBranch, Network, Cpu, Database, Lock, Eye, AlertTriangle, DollarSign, Clock, BarChart3, LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';


const AnimatedBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    let time = 0;
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.baseX = this.x;
        this.baseY = this.y;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.radius = Math.random() * 1.5 + 0.5;
        this.opacity = Math.random() * 0.3 + 0.1;
        this.color = Math.random() > 0.7 ? '59, 130, 246' : Math.random() > 0.4 ? '139, 92, 246' : '6, 182, 212';
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }
      
      update(time) {
        this.x += this.vx;
        this.y += this.vy;
        
        const dx = this.x - this.baseX;
        const dy = this.y - this.baseY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 100) {
          this.vx *= -0.5;
          this.vy *= -0.5;
        }
        
        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
        
        this.currentOpacity = this.opacity * (0.7 + 0.3 * Math.sin(time * this.pulseSpeed + this.pulsePhase));
      }
      
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.color}, ${this.currentOpacity})`;
        ctx.fill();
      }
    }
    
    for (let i = 0; i < 200; i++) {
      particles.push(new Particle());
    }
    
    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      time += 1;
      
      particles.forEach(particle => {
        particle.update(time);
        particle.draw();
      });
      
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            const opacity = (1 - distance / 150) * 0.08;
            ctx.strokeStyle = `rgba(59, 130, 246, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    window.addEventListener('resize', resizeCanvas);
    
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full -z-10" />;
};

const JoinModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    volume: '',
    useCase: '',
    interests: [],
    email: '',
    twitter: '',
    telegram: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  // REPLACE THIS WITH YOUR APPS SCRIPT WEB APP URL
  const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyTafC0NXPfsl3mCuMtMlOK1btYAk12x1-Ltjq8egIE8fUpeVJtv3ESHrw2IPsy0xM8jQ/exec';

  if (!isOpen) return null;
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const response = await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      setSubmitMessage({ 
        type: 'success', 
        text: 'Successfully joined the waitlist! We\'ll be in touch soon.' 
      });
      
      setTimeout(() => {
        setFormData({
          volume: '',
          useCase: '',
          interests: [],
          email: '',
          twitter: '',
          telegram: ''
        });
        setSubmitMessage({ type: '', text: '' });
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage({ 
        type: 'error', 
        text: 'Failed to submit. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const interestOptions = [
    'DeFi', 'NFTs', 'Gaming', 'DAOs', 'Infrastructure', 'Trading Tools'
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl animate-fadeIn overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4 py-12">
        <div className="relative w-full max-w-4xl bg-black border border-blue-500/30 shadow-2xl animate-slideUp">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 md:top-6 md:right-6 p-2 text-gray-400 hover:text-white transition-colors z-10 hover:bg-white/5"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
          
          <div className="relative p-6 md:p-12 max-h-[85vh] overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-3xl md:text-5xl font-light mb-4 text-white tracking-tight">
                Join The Build
              </h2>
              <p className="text-lg md:text-xl text-gray-400 font-light">
                We're assembling 250 people who understand what's broken and want to fix it.
              </p>
            </div>
            
            {submitMessage.text && (
              <div className={`mb-6 p-4 border ${
                submitMessage.type === 'success' 
                  ? 'bg-green-500/10 border-green-500/30 text-green-400' 
                  : 'bg-red-500/10 border-red-500/30 text-red-400'
              }`}>
                <p className="font-mono text-sm">{submitMessage.text}</p>
              </div>
            )}
            
            <div className="space-y-6 md:space-y-8">
              <div>
                <label className="block text-xs md:text-sm font-mono text-gray-400 mb-3 uppercase tracking-wider">
                  Monthly Trading Volume
                </label>
               <select
                  value={formData.volume}
                  onChange={(e) => setFormData({...formData, volume: e.target.value})}
                  className="w-full bg-white/5 border border-gray-700 text-white px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:border-blue-500 transition-colors font-mono text-sm"
                  required
                  disabled={isSubmitting}
                  style={{
                    colorScheme: 'dark'
                  }}
                >
                  <option value="" style={{ backgroundColor: '#000', color: '#fff' }}>Select range</option>
                  <option value="Less then $100K" style={{ backgroundColor: '#000', color: '#fff' }}>Less then $100K</option>
                  <option value="100k-500k" style={{ backgroundColor: '#000', color: '#fff' }}>$100k - $500k</option>
                  <option value="500k-1m" style={{ backgroundColor: '#000', color: '#fff' }}>$500k - $1M</option>
                  <option value="1m-5m" style={{ backgroundColor: '#000', color: '#fff' }}>$1M - $5M</option>
                  <option value="5m+" style={{ backgroundColor: '#000', color: '#fff' }}>$5M+</option>
                </select>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-mono text-gray-400 mb-3 uppercase tracking-wider">
                  Primary Use Case
                </label>
                <textarea
                  value={formData.useCase}
                  onChange={(e) => setFormData({...formData, useCase: e.target.value})}
                  className="w-full bg-white/5 border border-gray-700 text-white px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:border-blue-500 transition-colors font-mono text-sm resize-none"
                  rows="4"
                  placeholder="What are you building or trading?"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-mono text-gray-400 mb-3 uppercase tracking-wider">
                  Interests
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {interestOptions.map((interest) => (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => handleInterestToggle(interest)}
                      className={`px-4 py-3 border font-mono text-sm transition-all ${
                        formData.interests.includes(interest)
                          ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                          : 'bg-white/5 border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}
                      disabled={isSubmitting}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-mono text-gray-400 mb-3 uppercase tracking-wider">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-white/5 border border-gray-700 text-white px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:border-blue-500 transition-colors font-mono text-sm"
                  placeholder="your@email.com"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs md:text-sm font-mono text-gray-400 mb-3 uppercase tracking-wider">
                    Twitter (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.twitter}
                    onChange={(e) => setFormData({...formData, twitter: e.target.value})}
                    className="w-full bg-white/5 border border-gray-700 text-white px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:border-blue-500 transition-colors font-mono text-sm"
                    placeholder="@username"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-mono text-gray-400 mb-3 uppercase tracking-wider">
                    Telegram (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.telegram}
                    onChange={(e) => setFormData({...formData, telegram: e.target.value})}
                    className="w-full bg-white/5 border border-gray-700 text-white px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:border-blue-500 transition-colors font-mono text-sm"
                    placeholder="@username"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-mono py-4 md:py-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider text-sm"
              >
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

// Demo wrapper

const MetricCard = ({ value, label, sublabel, icon: Icon }) => (
  <div className="relative group">
    <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="relative bg-black border border-gray-800 p-8 group-hover:border-blue-500/30 transition-all duration-500">
      <Icon className="text-blue-400 mb-6 opacity-60" size={32} strokeWidth={1.5} />
      <div className="text-5xl font-light text-white mb-3 font-mono tracking-tight">
        {value}
      </div>
      <div className="text-gray-400 font-mono text-sm uppercase tracking-wider mb-1">{label}</div>
      {sublabel && <div className="text-gray-600 text-xs font-mono">{sublabel}</div>}
    </div>
  </div>
);

export default function HomePage() {
  const [openModal, setOpenModal] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <AnimatedBackground />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 animate-fadeIn">
            <div className="inline-flex items-center gap-3 px-6 py-3 border border-blue-500/30 backdrop-blur-sm">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <p className="text-blue-400 font-mono text-sm uppercase tracking-wider">250 Builders • Open Build • Real Infrastructure</p>
            </div>
          </div>
          
          <h1 className="text-7xl md:text-9xl font-light mb-12 text-white leading-[0.9] tracking-tighter animate-fadeIn" style={{animationDelay: '0.2s'}}>
            Execution Infrastructure<br />
            <span className="text-gray-600">Built By Traders,</span><br />
            <span className="text-blue-400">For Traders</span>
          </h1>
          
          <div className="max-w-4xl mb-16 space-y-8 animate-fadeIn" style={{animationDelay: '0.4s'}}>
            <p className="text-2xl md:text-3xl text-gray-400 leading-relaxed font-light">
              We're not building another aggregator with cheap marketing claims.
            </p>
            <p className="text-2xl md:text-3xl text-white leading-relaxed font-light">
              We're building the execution layer DeFi actually needs—designed by people who move serious volume and understand what's broken.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 items-start mb-24 animate-fadeIn" style={{animationDelay: '0.6s'}}>
            <button
              onClick={() => setOpenModal(true)}
              className="group px-12 py-5 bg-white text-black font-mono text-sm uppercase tracking-wider hover:bg-gray-200 transition-all duration-300 flex items-center gap-3"
            >
              Join The Build
              <ArrowRight className="group-hover:translate-x-2 transition-transform" size={20} />
            </button>
            <Link
              to="/vision"
              className="inline-block px-12 py-5 border border-gray-700 font-mono text-sm uppercase tracking-wider hover:border-gray-600 hover:bg-white/5 transition-all duration-300 text-center"
            >
              Read The Vision
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-800 border border-gray-800 animate-fadeIn" style={{animationDelay: '0.8s'}}>
            <div className="bg-black p-6">
              <div className="text-3xl font-mono text-white mb-2">250</div>
              <div className="text-xs font-mono text-gray-500 uppercase tracking-wider">Total Spots</div>
            </div>
            <div className="bg-black p-6">
              <div className="text-3xl font-mono text-white mb-2">$100k+</div>
              <div className="text-xs font-mono text-gray-500 uppercase tracking-wider">Min Volume</div>
            </div>
            <div className="bg-black p-6">
              <div className="text-3xl font-mono text-white mb-2">0 VC</div>
              <div className="text-xs font-mono text-gray-500 uppercase tracking-wider">Money Raised</div>
            </div>
            <div className="bg-black p-6">
              <div className="text-3xl font-mono text-white mb-2">∞</div>
              <div className="text-xs font-mono text-gray-500 uppercase tracking-wider">Timeline Honest</div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Problem Section */}
      <section className="py-40 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24">
            <h2 className="text-6xl md:text-7xl font-light mb-8 text-white tracking-tight">
              You Know The Problem<br />
              <span className="text-gray-600">Isn't The Price Quote</span>
            </h2>
            <p className="text-2xl text-gray-400 font-light max-w-3xl">
              Every large trade is a calculated risk. You're not looking for "cheapest"—you're looking for reliable.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16 mb-24">
            <div className="space-y-8">
              <div className="border-l-2 border-red-500/30 pl-8">
                <h3 className="text-3xl font-light text-white mb-4">The $300k Trade</h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Quote says $297k out. You execute. You get $287k.
                </p>
                <div className="space-y-4 text-gray-500 font-mono text-sm">
                  <p>→ MEV bot front-ran you</p>
                  <p>→ Pool was imbalanced</p>
                  <p>→ Bridge timing was off</p>
                  <p>→ Market moved during execution</p>
                  <p>→ Gas spiked mid-transaction</p>
                </div>
                <div className="mt-8 p-6 bg-red-500/5 border border-red-500/20">
                  <p className="text-5xl font-mono text-red-400 mb-2">$10,000</p>
                  <p className="text-sm font-mono text-gray-500 uppercase tracking-wider">Gone. Again.</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="bg-white/5 border border-gray-800 p-8">
                <h4 className="text-xl font-mono text-gray-400 mb-6 uppercase tracking-wider">All Preventable</h4>
                <div className="space-y-6 text-gray-400">
                  <p className="leading-relaxed">
                    You've done this 15-20 times. Each time losing 2-4%. You've built spreadsheets tracking it.
                  </p>
                  <p className="leading-relaxed">
                    You've tried every aggregator. You've joined Telegram groups asking "anyone else seeing this?"
                  </p>
                  <p className="leading-relaxed text-white">
                    Everyone is. Nobody's fixing it.
                  </p>
                </div>
              </div>
              
              <div className="bg-blue-500/5 border border-blue-500/20 p-8">
                <p className="text-gray-400 leading-relaxed mb-4">
                  None of this showed up in the "quote." Because current infrastructure can't handle the reality of large-scale execution.
                </p>
                <p className="text-blue-400 font-mono text-sm">
                  It's not built for you.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-px bg-gray-800 border border-gray-800">
            <MetricCard 
              value="15-20x" 
              label="Trades With Losses" 
              sublabel="You're not imagining it"
              icon={TrendingUp}
            />
            <MetricCard 
              value="2-4%" 
              label="Lost Per Trade" 
              sublabel="Compounds fast"
              icon={Activity}
            />
            <MetricCard 
              value="$0" 
              label="Working Solutions" 
              sublabel="Until now"
              icon={Target}
            />
          </div>
        </div>
      </section>

      {/* Why It's Broken */}
      <section className="py-40 px-6 relative border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24">
            <h2 className="text-6xl md:text-7xl font-light mb-8 text-white tracking-tight">
              Why Current Infrastructure<br />
              <span className="text-gray-600">Is Fundamentally Broken</span>
            </h2>
          </div>
          
          <div className="mb-24 border border-gray-800 bg-black">
            <div className="p-12 border-b border-gray-800">
              <h3 className="text-3xl font-light text-white mb-8">Every Aggregator Is Optimized For:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  'Retail users doing $500 swaps',
                  'Transaction count (VC metrics)',
                  'Mobile-first UX',
                  'Marketing claims about "best price"'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-6 bg-white/5 border border-gray-800">
                    <div className="w-1 h-full bg-red-500/30" />
                    <p className="text-gray-300 font-mono text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-12">
              <p className="text-3xl font-light text-white mb-6">
                None of them are built for $100k+ cross-chain execution.
              </p>
              <p className="text-xl text-gray-400 font-light">
                Your $300k trade gets routed the same way as a $300 trade.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16 mb-24">
            <div className="border border-red-500/20 bg-red-500/5">
              <div className="p-10 border-b border-red-500/20">
                <h4 className="text-2xl font-mono text-red-400 mb-6 uppercase tracking-wider">Static Routing</h4>
                <div className="space-y-4 font-mono text-sm text-gray-400">
                  <p>1. Check prices once</p>
                  <p>2. Pick one route</p>
                  <p>3. Execute</p>
                  <p>4. Pray nothing changes</p>
                </div>
              </div>
              <div className="p-10">
                <p className="text-red-400 font-mono text-lg mb-4">
                  But everything changes in 15 seconds.
                </p>
                <div className="space-y-3 text-gray-500 text-sm">
                  <p>• Pools shift from large trades</p>
                  <p>• Gas spikes from congestion</p>
                  <p>• MEV bots are watching</p>
                  <p>• Your trade leaks information</p>
                  <p>• Bridge timing varies</p>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-800 bg-black">
              <div className="p-10 border-b border-gray-800">
                <h4 className="text-2xl font-mono text-gray-400 mb-6 uppercase tracking-wider">The Real Cost</h4>
              </div>
              <div className="p-10 space-y-8">
                <div>
                  <p className="text-gray-500 font-mono text-xs uppercase tracking-wider mb-2">On $500k Monthly Volume</p>
                  <p className="text-5xl font-mono text-white mb-2">$102,000</p>
                  <p className="text-gray-500 text-sm">Lost per year to execution costs</p>
                </div>
                <div>
                  <p className="text-gray-500 font-mono text-xs uppercase tracking-wider mb-2">On $2M Monthly Volume</p>
                  <p className="text-5xl font-mono text-white mb-2">$408,000</p>
                  <p className="text-gray-500 text-sm">Lost per year to execution costs</p>
                </div>
                <div className="pt-6 border-t border-gray-800">
                  <p className="text-blue-400 font-mono text-sm">
                    This is money that should be yours.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border border-gray-800 bg-black p-12 text-center">
            <p className="text-3xl font-light text-white mb-6">
              And nobody's building adaptive infrastructure
            </p>
            <p className="text-xl text-gray-400 font-light">
              Because there's no money in it from VCs who want retail DAUs
            </p>
          </div>
        </div>
      </section>
      
      {/* The Vision */}
      <section className="py-40 px-6 relative border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24">
            <h2 className="text-6xl md:text-7xl font-light mb-8 text-white tracking-tight">
              A Different Approach<br />
              <span className="text-blue-400">To Execution</span>
            </h2>
            <p className="text-2xl text-gray-400 font-light max-w-3xl">
              Not another aggregator. The execution infrastructure that serious capital actually needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16 mb-32">
            <div className="space-y-12">
              <div className="border-l-2 border-blue-500/30 pl-8">
                <div className="flex items-center gap-4 mb-4">
                  <Layers className="text-blue-400" size={32} strokeWidth={1.5} />
                  <h3 className="text-2xl font-mono text-white uppercase tracking-wider">Parallel Execution</h3>
                </div>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Multiple paths simultaneously. If one fails, others continue. Exploit opportunities across pools at once.
                </p>
                <div className="space-y-2 font-mono text-sm text-gray-500">
                  <p>→ Don't wait for sequential routing</p>
                  <p>→ Exploit price differences across pools</p>
                  <p>→ Path independence = resilience</p>
                </div>
              </div>
              
              <div className="border-l-2 border-blue-500/30 pl-8">
                <div className="flex items-center gap-4 mb-4">
                  <Activity className="text-blue-400" size={32} strokeWidth={1.5} />
                  <h3 className="text-2xl font-mono text-white uppercase tracking-wider">Real-Time Adaptation</h3>
                </div>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Each path monitors its own conditions. Recalculates independently in 0.8 seconds. Reroutes before problems cost you.
                </p>
                <div className="space-y-2 font-mono text-sm text-gray-500">
                  <p>→ Sub-second recalculation</p>
                  <p>→ Independent path intelligence</p>
                  <p>→ Proactive problem avoidance</p>
                </div>
              </div>
              
              <div className="border-l-2 border-blue-500/30 pl-8">
                <div className="flex items-center gap-4 mb-4">
                  <Network className="text-blue-400" size={32} strokeWidth={1.5} />
                  <h3 className="text-2xl font-mono text-white uppercase tracking-wider">Cross-Chain Intelligence</h3>
                </div>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Real-time bridge health monitoring. Optimal path selection per trade size. Automatic congestion routing.
                </p>
                <div className="space-y-2 font-mono text-sm text-gray-500">
                  <p>→ Bridge reliability scoring</p>
                  <p>→ Dynamic path weighting</p>
                  <p>→ Congestion prediction</p>
                </div>
              </div>
            </div>

            
            
            <div className="border border-gray-800 bg-black">
              <div className="p-10 border-b border-gray-800">
                <h3 className="text-3xl font-light text-white mb-6">
                  How It Actually Works
                </h3>
                <p className="text-gray-400 font-mono text-sm uppercase tracking-wider">
                  Your $400k USDC → ETH Cross-Chain Trade
                </p>
              </div>
            
              <div className="divide-y divide-gray-800">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0 font-mono text-blue-400 text-sm">
                    1
                  </div>
                  <div className="flex-grow">
                    <h4 className="text-lg font-mono text-white mb-3 uppercase tracking-wider">System Analyzes</h4>
                    <p className="text-gray-400 text-sm mb-4">
                      Parallel path decomposition based on current liquidity, gas, bridge health, and market conditions.
                    </p>
                    <div className="space-y-2 text-xs font-mono text-gray-500">
                      <p>Path 1: $140k → Bridge A → Uniswap V3</p>
                      <p>Path 2: $120k → Bridge B → Curve</p>
                      <p>Path 3: $80k → Native swap → Bridge ETH</p>
                      <p>Path 4: $60k → Bridge C → Aerodrome</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-8 h-8 bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center flex-shrink-0 font-mono text-yellow-400 text-sm">
                    2
                  </div>
                  <div className="flex-grow">
                    <h4 className="text-lg font-mono text-white mb-3 uppercase tracking-wider">Path 2 Detects Issue</h4>
                    <p className="text-gray-400 text-sm mb-4">
                      Mid-execution: Curve pool hit by large trade, imbalanced. System recalculates in 0.8s.
                    </p>
                    <div className="space-y-2 text-xs font-mono">
                      <p className="text-red-400">✗ Curve pool imbalanced</p>
                      <p className="text-yellow-400">⟳ Recalculating...</p>
                      <p className="text-green-400">✓ Rerouted through Balancer</p>
                      <p className="text-blue-400">→ Saved $2,100</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-8 h-8 bg-purple-500/10 border border-purple-500/30 flex items-center justify-center flex-shrink-0 font-mono text-purple-400 text-sm">
                    3
                  </div>
                  <div className="flex-grow">
                    <h4 className="text-lg font-mono text-white mb-3 uppercase tracking-wider">Path 3 Gas Spike</h4>
                    <p className="text-gray-400 text-sm mb-4">
                      Native chain gas spikes 3x. Independent recalculation and split.
                    </p>
                    <div className="space-y-2 text-xs font-mono">
                      <p className="text-red-400">✗ Gas 3x normal</p>
                      <p className="text-yellow-400">⟳ Splitting execution</p>
                      <p className="text-green-400">✓ Partial through cheaper pools</p>
                      <p className="text-green-400">✓ Remainder via Path 1 bridge</p>
                      <p className="text-blue-400">→ Saved $840 in gas</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-10 bg-green-500/5">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-8 h-8 bg-green-500/10 border border-green-500/30 flex items-center justify-center flex-shrink-0 font-mono text-green-400 text-sm">
                    ✓
                  </div>
                  <div className="flex-grow">
                    <h4 className="text-lg font-mono text-white mb-6 uppercase tracking-wider">All Paths Complete</h4>
                    <div className="grid grid-cols-3 gap-6 mb-6">
                      <div>
                        <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">Received</p>
                        <p className="text-2xl font-mono text-white">$396,200</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">Cost</p>
                        <p className="text-2xl font-mono text-white">0.95%</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">vs Static</p>
                        <p className="text-2xl font-mono text-red-400">$388,400</p>
                      </div>
                    </div>
                    <div className="pt-6 border-t border-green-500/20">
                      <p className="text-3xl font-mono text-green-400">+$7,800</p>
                      <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mt-1">Better than aggregators</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border border-gray-800 bg-black p-16">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-4xl font-light text-white mb-8">
              This isn't "slightly better"
            </p>
            <p className="text-2xl text-gray-400 font-light mb-12">
              This is infrastructure that fundamentally changes your economics
            </p>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="border border-gray-800 p-10">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-4">$500k Monthly Volume</p>
                <p className="text-6xl font-mono text-green-400 mb-4">$102k</p>
                <p className="text-gray-400 font-mono text-sm">more per year</p>
              </div>
              <div className="border border-gray-800 p-10">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-4">$2M Monthly Volume</p>
                <p className="text-6xl font-mono text-green-400 mb-4">$408k</p>
                <p className="text-gray-400 font-mono text-sm">more per year</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Building Together */}
      <section className="py-40 px-6 relative border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24">
            <h2 className="text-6xl md:text-7xl font-light mb-8 text-white tracking-tight">
              Why We're Building<br />
              <span className="text-gray-600">It Together</span>
            </h2>
            <p className="text-2xl text-gray-400 font-light max-w-3xl">
              Because the people who need it should build it.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16 mb-24">
            <div className="border border-red-500/20 bg-red-500/5">
              <div className="p-10 border-b border-red-500/20">
                <h3 className="text-2xl font-mono text-red-400 uppercase tracking-wider">Most Protocols</h3>
              </div>
              <div className="p-10 space-y-4 font-mono text-sm text-gray-400">
                <p>1. Founders build in secret</p>
                <p>2. Raise VC money</p>
                <p>3. Launch with hype</p>
                <p>4. Product doesn't match reality</p>
                <p>5. Token dumps</p>
                <p>6. Move on to next thing</p>
              </div>
            </div>
            
            <div className="border border-green-500/20 bg-green-500/5">
              <div className="p-10 border-b border-green-500/20">
                <h3 className="text-2xl font-mono text-green-400 uppercase tracking-wider">We're Different</h3>
              </div>
              <div className="p-10 space-y-4 font-mono text-sm text-gray-400">
                <p>1. 250 people who actually trade this volume</p>
                <p>2. Tell us what should be built</p>
                <p>3. We build it transparently</p>
                <p>4. You test with real money</p>
                <p>5. Launch when it actually works</p>
                <p>6. Everyone stays aligned</p>
              </div>
            </div>
          </div>
          
          <div className="border border-blue-500/20 bg-blue-500/5 p-16">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-4xl font-light text-white mb-8">
                This is a community build
              </p>
              <p className="text-xl text-gray-400 font-light leading-relaxed">
                Not a product launch. Not a token sale. A group of people fixing the infrastructure that's been broken since day one.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Who Belongs Here */}
      <section className="py-40 px-6 relative border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24">
            <h2 className="text-6xl md:text-7xl font-light mb-8 text-white tracking-tight">
              Who Belongs Here
            </h2>
            <p className="text-2xl text-gray-400 font-light max-w-3xl">
              This is for people who trade serious volume and understand the problem.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 mb-24">
            <div className="border border-gray-800 bg-black">
              <div className="p-8 border-b border-gray-800">
                <h3 className="text-xl font-mono text-white uppercase tracking-wider">You Trade</h3>
              </div>
              <div className="p-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">$100k+ monthly volume</p>
                    <p className="text-sm text-gray-500 font-mono">(often way more)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">Cross-chain regularly</p>
                    <p className="text-sm text-gray-500 font-mono">Where execution matters most</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">With capital that matters</p>
                    <p className="text-sm text-gray-500 font-mono">Where 2% is significant</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-800 bg-black">
              <div className="p-8 border-b border-gray-800">
                <h3 className="text-xl font-mono text-white uppercase tracking-wider">You Know</h3>
              </div>
              <div className="p-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">What MEV feels like</p>
                    <p className="text-sm text-gray-500 font-mono">You've been hit</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">Quote vs execution gap</p>
                    <p className="text-sm text-gray-500 font-mono">It's always there</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">Pool depth matters</p>
                    <p className="text-sm text-gray-500 font-mono">For large trades</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">Aggregators are built for retail</p>
                    <p className="text-sm text-gray-500 font-mono">Not for you</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-800 bg-black">
              <div className="p-8 border-b border-gray-800">
                <h3 className="text-xl font-mono text-white uppercase tracking-wider">You've Done</h3>
              </div>
              <div className="p-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">Tried every aggregator</p>
                    <p className="text-sm text-gray-500 font-mono">None work for your size</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">Built your own tools</p>
                    <p className="text-sm text-gray-500 font-mono">To compare prices</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">Lost money you can't prevent</p>
                    <p className="text-sm text-gray-500 font-mono">Over and over</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full bg-blue-500/30 mt-1" />
                  <div>
                    <p className="text-white mb-2">Thought "someone should fix this"</p>
                    <p className="text-sm text-gray-500 font-mono">That's why you're here</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border border-blue-500/20 bg-blue-500/5 p-16">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-3xl font-light text-white mb-12 text-center">We're Seeing Interest From:</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-400" />
                    <p className="text-gray-300">DAO treasury managers tired of explaining losses</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-400" />
                    <p className="text-gray-300">Traders who've built comparison scripts</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-400" />
                    <p className="text-gray-300">Market makers routing $1M+ daily</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-400" />
                    <p className="text-gray-300">Yield farmers checking 4 aggregators per trade</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-400" />
                    <p className="text-gray-300">Anyone moving serious capital cross-chain</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-400" />
                    <p className="text-gray-300">People tired of infrastructure built for someone else</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Timeline */}
      <section className="py-40 px-6 relative border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24">
            <h2 className="text-6xl md:text-7xl font-light mb-8 text-white tracking-tight">
              The Timeline
            </h2>
            <p className="text-2xl text-gray-400 font-light max-w-3xl">
              From assembly to becoming the standard for serious capital.
            </p>
          </div>
          
          <div className="space-y-px bg-gray-800">
            {[
              {
                phase: 'Months 1-3',
                title: 'Foundation',
                items: ['250 people assembled', 'Core builders shape direction', 'Architecture defined', 'Development begins']
              },
              {
                phase: 'Months 4-6',
                title: 'Build & Test',
                items: ['Core development', 'Beta group tests with real trades', 'Iterate based on feedback', 'Prove the approach works']
              },
              {
                phase: 'Months 7-9',
                title: 'Scale & Validate',
                items: ['Early community access', 'Scale testing', 'Cross-chain expansion', 'Prepare for launch']
              },
              {
                phase: 'Month 10',
                title: 'Launch',
                items: ['Token launches', 'Public access opens', 'Your vesting begins', 'Infrastructure goes live']
              },
              {
                phase: 'Months 11-24',
                title: 'Become The Standard',
                items: ['20+ chains supported', 'Protocol integrations', 'Wallets build on top', 'The execution layer DeFi needed']
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-black border-l-2 border-blue-500/30 hover:border-blue-500 transition-colors">
                <div className="p-10">
                  <div className="flex items-start gap-8">
                    <div className="w-32 flex-shrink-0">
                      <p className="text-sm font-mono text-gray-500 uppercase tracking-wider">{item.phase}</p>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-2xl font-light text-white mb-6">{item.title}</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {item.items.map((subItem, subIdx) => (
                          <div key={subIdx} className="flex items-center gap-3">
                            <div className="w-1 h-1 bg-blue-400" />
                            <p className="text-gray-400 text-sm">{subItem}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* What You Get */}
      <section className="py-40 px-6 relative border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24">
            <h2 className="text-6xl md:text-7xl font-light mb-8 text-white tracking-tight">
              What You Get
            </h2>
            <p className="text-2xl text-gray-400 font-light max-w-3xl">
              Based on your volume and involvement level.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 mb-24">
            <div className="border border-gray-800 bg-black">
              <div className="p-8 border-b border-gray-800">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-4">Core Builders</p>
                <p className="text-4xl font-mono text-white mb-2">10 spots</p>
                <p className="text-gray-500 font-mono text-sm">$2M+ monthly volume</p>
              </div>
              <div className="p-8 space-y-4 text-sm text-gray-400">
                <p>→ Direct line to founders</p>
                <p>→ Real say in product decisions</p>
                <p>→ Shape the entire direction</p>
                <p>→ Deepest involvement</p>
                <p>→ Best token terms</p>
              </div>
            </div>
            
            <div className="border border-gray-800 bg-black">
              <div className="p-8 border-b border-gray-800">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-4">Beta Group</p>
                <p className="text-4xl font-mono text-white mb-2">40 spots</p>
                <p className="text-gray-500 font-mono text-sm">$500k+ monthly volume</p>
              </div>
              <div className="p-8 space-y-4 text-sm text-gray-400">
                <p>→ Test with real money</p>
                <p>→ Help break it and improve it</p>
                <p>→ Early access to everything</p>
                <p>→ Beta community</p>
                <p>→ Better token terms</p>
              </div>
            </div>
            
            <div className="border border-gray-800 bg-black">
              <div className="p-8 border-b border-gray-800">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-4">Early Community</p>
                <p className="text-4xl font-mono text-white mb-2">200 spots</p>
                <p className="text-gray-500 font-mono text-sm">$100k+ monthly volume</p>
              </div>
              <div className="p-8 space-y-4 text-sm text-gray-400">
                <p>→ Use it early</p>
                <p>→ Give feedback</p>
                <p>→ 2 weeks before public</p>
                <p>→ Community access</p>
                <p>→ Good token terms</p>
              </div>
            </div>
          </div>
          
          <div className="border border-gray-800 bg-black p-16">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-3xl font-light text-white mb-6">
                This isn't a token sale
              </p>
              <p className="text-xl text-gray-400 font-light mb-4">
                It's alignment.
              </p>
              <p className="text-gray-500 font-mono text-sm">
                Infrastructure that needs to work for years requires everyone aligned long-term.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="py-40 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-7xl md:text-9xl font-light mb-12 text-white leading-[0.9] tracking-tighter">
              Join Us
            </h2>
            
            <p className="text-3xl font-light text-white mb-8">
              250 spots to build execution infrastructure that actually works
            </p>
            
            <p className="text-xl text-gray-400 font-light mb-16 max-w-3xl mx-auto leading-relaxed">
              For people who trade serious volume. Who understand the problem. Who want to be part of fixing it.
            </p>
            
            <button
              onClick={() => setOpenModal(true)}
              className="group px-16 py-6 bg-white text-black font-mono text-sm uppercase tracking-wider hover:bg-gray-200 transition-all duration-300 flex items-center gap-4 mx-auto mb-20"
            >
              Join The Build
              <ArrowRight className="group-hover:translate-x-2 transition-transform" size={22} />
            </button>
            
            <div className="grid grid-cols-3 gap-px bg-gray-800 border border-gray-800 mb-20">
              <div className="bg-black p-8">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-3">Core Builders</p>
                <p className="text-3xl font-mono text-white mb-2">10</p>
                <p className="text-xs font-mono text-gray-600">$2M+ volume</p>
              </div>
              <div className="bg-black p-8">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-3">Beta Group</p>
                <p className="text-3xl font-mono text-white mb-2">40</p>
                <p className="text-xs font-mono text-gray-600">$500k+ volume</p>
              </div>
              <div className="bg-black p-8">
                <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-3">Early Community</p>
                <p className="text-3xl font-mono text-white mb-2">200</p>
                <p className="text-xs font-mono text-gray-600">$100k+ volume</p>
              </div>
            </div>
          </div>
          
          <div className="border border-blue-500/20 bg-blue-500/5 p-12 text-center">
            <p className="text-4xl font-light text-white mb-8">This is your tribe.</p>
            <p className="text-xl text-gray-400 font-light mb-10">
              Applications open. Spots filling.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center font-mono text-sm">
              <a href="amin29199@gmail.com" className="text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-wider">
                Founder's email
              </a>
              <span className="hidden sm:block text-gray-700">|</span>
              {/* <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-wider">
                @twitter
              </a>
              <span className="hidden sm:block text-gray-700">|</span>
              <a href="https://t.me" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-wider">
                telegram
              </a> */}
              <a href="https://www.linkedin.com/in/mostafa-amin-2985b0395/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-wider">
                Founder's Linkedin
              </a>
            </div>
          </div>
        </div>
      </section>
      
      <JoinModal isOpen={openModal} onClose={() => setOpenModal(false)} />
      
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 1s ease-out forwards;
        }
        
        .animate-slideUp {
          animation: slideUp 0.4s ease-out forwards;
        }
        
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
      `}</style>
    </div>
  );
}